import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../api/axiosConfig'; // Подключаем API

const ProfileSettings = () => {
    const { username, userData } = useAuth();
    const fileInputRef = useRef(null);
    
    // Состояния профиля
    const [formData, setFormData] = useState({
        phone: userData?.phone || '',
        address: userData?.address || '',
        bio: userData?.bio || ''
    });
    
    // Состояние для аватарки (локальный предпросмотр)
    const [avatarPreview, setAvatarPreview] = useState(null);

    // НОВОЕ: Состояние загрузки при сохранении
    const [isSaving, setIsSaving] = useState(false);

    // Состояния для смены пароля
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    // Состояния видимости паролей (глазики)
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const currentAvatar = userData?.avatar ? (userData.avatar.startsWith('http') ? userData.avatar : `${baseUrl}${userData.avatar}`) : null;

    // ОБНОВЛЕНО: Обработчик загрузки фото (теперь с отправкой на сервер)
    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Показываем фото пользователю
            const previewUrl = URL.createObjectURL(file);
            setAvatarPreview(previewUrl);
            
            // Сразу отправляем фото на бэкенд
            const data = new FormData();
            data.append('avatar', file);
            try {
                await API.post('/api/profile/upload-avatar/', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                window.location.reload();
            } catch (err) {
                alert("Ошибка при загрузке фото на сервер");
            }
        }
    };

    // НОВОЕ: Главная функция сохранения профиля и пароля
    const handleSave = async () => {
        setIsSaving(true);
        try {
            // 1. Сохраняем текстовые данные профиля
            await API.patch('/api/profile/update/', formData);
            
            // 2. Если включена смена пароля - отправляем и её
            if (isChangingPassword) {
                // Проверяем совпадение новых паролей
                if (passwordData.newPassword !== passwordData.confirmPassword) {
                    alert("Новые пароли не совпадают!");
                    setIsSaving(false);
                    return;
                }
                // Отправляем на сервер
                await API.post('/api/profile/change-password/', {
                    old_password: passwordData.oldPassword,
                    new_password: passwordData.newPassword
                });
                
                // Очищаем поля пароля после успеха
                setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
                setIsChangingPassword(false);
            }
            
            alert("Настройки успешно сохранены!");
            window.location.reload();
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.detail || "Проверьте введенные данные";
            alert("Ошибка при сохранении: " + errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    // Компонент иконки глазика
    const EyeIcon = ({ isVisible, toggleVisibility }) => (
        <button 
            type="button" 
            onClick={toggleVisibility}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors focus:outline-none"
        >
            {isVisible ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
            ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            )}
        </button>
    );

    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 font-sans">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-3xl font-black text-slate-900 mb-8">Настройки профиля</h1>

                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-700"></div>
                    
                    <div className="px-8 pb-8 relative">
                        {/* Аватарка (Кликабельная) */}
                        <div className="flex justify-between items-end -mt-12 mb-8">
                            <div 
                                className="relative group cursor-pointer"
                                onClick={() => fileInputRef.current.click()}
                            >
                                <div className="w-24 h-24 bg-white rounded-2xl p-1 shadow-md overflow-hidden">
                                    {avatarPreview ? (
                                        <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover rounded-xl" />
                                ) : currentAvatar ? (
                                    /* === ИСПОЛЬЗУЕМ currentAvatar ВМЕСТО userData.avatar === */
                                        <img src={currentAvatar} alt="Avatar" className="w-full h-full object-cover rounded-xl" />
                                ) : (
                                        <div className="w-full h-full bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 text-3xl font-black">
                                        {username ? username.substring(0, 2).toUpperCase() : 'УЗ'}
                                        </div>
                                )}                        
                                </div>
                                <div className="absolute inset-0 bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <span className="text-white text-xs font-bold text-center px-2">Изменить<br/>фото</span>
                                </div>
                                {/* Скрытый input для загрузки файла */}
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    ref={fileInputRef} 
                                    onChange={handleAvatarChange} 
                                    className="hidden" 
                                />
                            </div>
                            
                            <div className="pb-2">
                                <span className="bg-slate-100 text-slate-600 font-bold text-xs px-3 py-1.5 rounded-lg uppercase tracking-wider">
                                    {userData?.role === 'STUDENT' ? 'Учащийся' : userData?.role === 'TEACHER' ? 'Преподаватель' : 'Администратор'}
                                </span>
                            </div>
                        </div>

                        {/* Основные настройки */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Логин</label>
                                <input type="text" value={username || ''} disabled className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 font-medium" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Email</label>
                                <input type="email" value={userData?.email || 'email@example.com'} disabled className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 font-medium" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Номер телефона</label>
                                <input type="tel" placeholder="+994 (__) ___-__-__" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition-all" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Адрес / Город</label>
                                <input type="text" placeholder="Например: Баку, Азербайджан" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition-all" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Обо мне</label>
                                <textarea rows="3" placeholder="Напишите немного о себе..." value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none transition-all resize-none"></textarea>
                            </div>
                        </div>

                        {/* Блок смены пароля (открывается по кнопке) */}
                        {isChangingPassword && (
                            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 mb-8 animate-slide-in-right">
                                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-4">Смена пароля</h3>
                                <div className="space-y-4">
                                    <div className="relative">
                                        <input 
                                            type={showOldPassword ? "text" : "password"} 
                                            placeholder="Старый пароль" 
                                            value={passwordData.oldPassword}
                                            onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})}
                                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none pr-12"
                                        />
                                        <EyeIcon isVisible={showOldPassword} toggleVisibility={() => setShowOldPassword(!showOldPassword)} />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="relative">
                                            <input 
                                                type={showNewPassword ? "text" : "password"} 
                                                placeholder="Новый пароль" 
                                                value={passwordData.newPassword}
                                                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none pr-12"
                                            />
                                            <EyeIcon isVisible={showNewPassword} toggleVisibility={() => setShowNewPassword(!showNewPassword)} />
                                        </div>
                                        <div className="relative">
                                            <input 
                                                type={showConfirmPassword ? "text" : "password"} 
                                                placeholder="Повторите новый пароль" 
                                                value={passwordData.confirmPassword}
                                                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none pr-12"
                                            />
                                            <EyeIcon isVisible={showConfirmPassword} toggleVisibility={() => setShowConfirmPassword(!showConfirmPassword)} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ОБНОВЛЕНО: Кнопки действий (Добавлен обработчик handleSave) */}
                        <div className="pt-6 border-t border-slate-100 flex justify-end gap-3">
                            <button 
                                onClick={() => setIsChangingPassword(!isChangingPassword)}
                                className={`px-6 py-2.5 rounded-xl font-bold transition-colors ${isChangingPassword ? 'bg-slate-200 text-slate-700' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}
                            >
                                {isChangingPassword ? 'Отмена' : 'Изменить пароль'}
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={isSaving}
                                className={`px-6 py-2.5 rounded-xl font-bold text-white shadow-sm transition-colors ${isSaving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {isSaving ? 'Сохранение...' : 'Сохранить изменения'}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileSettings;